import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "./firebaseConfig";

const CollectionName =
  process.env.REACT_APP_FIREBASE_GAMES_COLLECTION || "games";

// ============================================================================
// GAME STALENESS UTILITY
// ============================================================================
// Self-contained utility for determining if a game is too old to reconnect to.
// This is a read-only check that does NOT modify any game data or affect other systems.
//
// THRESHOLD: 20 minutes (longest time control is 10 minutes, so 20 is safe buffer)
//
// TIMESTAMP SOURCES (checked in order of reliability):
// 1. lastMoveTimestamp - Updated on every move (time control system)
// 2. lightPlayerLastSeen / darkPlayerLastSeen - Presence heartbeats
// 3. No timestamps = stale (old games pre-dating these features)
// ============================================================================

const STALE_GAME_THRESHOLD_MS = 20 * 60 * 1000; // 20 minutes

/**
 * Determines if a game is too old (stale) to be worth reconnecting to.
 *
 * A game is considered stale if no activity has occurred in the last 20 minutes.
 * This is a pure read-only check - it does NOT modify any data.
 *
 * @param gameData - The raw Firebase game document data
 * @returns true if the game is stale (should NOT show reconnect modal)
 */
export function isGameStale(gameData: {
  lastMoveTimestamp?: number;
  lightPlayerLastSeen?: number;
  darkPlayerLastSeen?: number;
}): boolean {
  const now = Date.now();

  // Priority 1: Check lastMoveTimestamp (most accurate - updated on every move)
  if (gameData.lastMoveTimestamp && typeof gameData.lastMoveTimestamp === 'number') {
    const age = now - gameData.lastMoveTimestamp;
    return age > STALE_GAME_THRESHOLD_MS;
  }

  // Priority 2: Check player presence timestamps (heartbeat system)
  const lightLastSeen = typeof gameData.lightPlayerLastSeen === 'number' ? gameData.lightPlayerLastSeen : 0;
  const darkLastSeen = typeof gameData.darkPlayerLastSeen === 'number' ? gameData.darkPlayerLastSeen : 0;
  const mostRecentPresence = Math.max(lightLastSeen, darkLastSeen);

  if (mostRecentPresence > 0) {
    const age = now - mostRecentPresence;
    return age > STALE_GAME_THRESHOLD_MS;
  }

  // No timestamps available - game predates these features, definitely stale
  return true;
}

/**
 * Returns the age of a game in milliseconds, or null if no timestamps available.
 * Useful for debugging/logging.
 */
export function getGameAge(gameData: {
  lastMoveTimestamp?: number;
  lightPlayerLastSeen?: number;
  darkPlayerLastSeen?: number;
}): { ageMs: number; source: 'lastMove' | 'presence' } | null {
  const now = Date.now();

  if (gameData.lastMoveTimestamp && typeof gameData.lastMoveTimestamp === 'number') {
    return { ageMs: now - gameData.lastMoveTimestamp, source: 'lastMove' };
  }

  const lightLastSeen = typeof gameData.lightPlayerLastSeen === 'number' ? gameData.lightPlayerLastSeen : 0;
  const darkLastSeen = typeof gameData.darkPlayerLastSeen === 'number' ? gameData.darkPlayerLastSeen : 0;
  const mostRecentPresence = Math.max(lightLastSeen, darkLastSeen);

  if (mostRecentPresence > 0) {
    return { ageMs: now - mostRecentPresence, source: 'presence' };
  }

  return null;
}

// ============================================================================
// END GAME STALENESS UTILITY
// ============================================================================

// Function to listen for changes in a specific document
export function listenForDocumentChanges(
  documentId: string,
  callback: (data: any | null) => void
) {
  const docRef = doc(db, CollectionName, documentId);
  return onSnapshot(docRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      const data = {
        id: docSnapshot.id,
        ...docSnapshot.data(),
      } as any;
      callback(data);
    } else {
      callback(null);
    }
  });
}

// Function to update or create a document
export async function updateGamesDocument(
  documentId: string,
  data: Partial<any>
): Promise<void> {
  const docRef = doc(db, CollectionName, documentId);
  await setDoc(docRef, data, { merge: true });
}

export async function querySearchingGames(): Promise<
  { id: string; data: any }[]
> {
  const gamesCollection = collection(db, CollectionName);
  const q = query(gamesCollection, where("state", "==", "Searching"));

  try {
    const querySnapshot = await getDocs(q);
    const searchingGames: { id: string; data: any }[] = [];
    querySnapshot.forEach((doc) => {
      searchingGames.push({ id: doc.id, data: { ...doc.data() } });
    });
    return searchingGames;
  } catch (error) {
    throw error;
  }
}

/**
 * Query for games that are currently being played (spectatable)
 * Returns games where both players are connected and game is in progress
 * Uses isGameStale() to filter out old/abandoned games (>20 min without activity)
 */
export async function queryActiveGames(): Promise<{ id: string; data: any }[]> {
  const gamesCollection = collection(db, CollectionName);
  const activeGames: { id: string; data: any }[] = [];

  // Query directly for games in "Playing" state (most common spectatable state)
  const playingStates = ['Playing'];


  try {
    for (const state of playingStates) {
      const q = query(
        gamesCollection,
        where("state", "==", state),
        limit(20)
      );

      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const gameId = docSnapshot.id;

        // Must have both players
        if (!data.darkPlayer) {
          return;
        }

        // Check lightWon - must be empty/undefined/null (not explicitly true/false)
        if (data.lightWon === true || data.lightWon === false) {
          return;
        }

        // Use staleness utility to filter out old/abandoned games
        if (isGameStale(data)) {
          const age = getGameAge(data);
          const ageStr = age ? `${Math.round(age.ageMs / 60000)} min via ${age.source}` : 'no timestamps';
          return;
        }

        const age = getGameAge(data);
        const ageStr = age ? `${Math.round(age.ageMs / 1000)}s via ${age.source}` : 'unknown';
        activeGames.push({ id: docSnapshot.id, data: { ...data } });
      });
    }

    return activeGames;
  } catch (error) {
    throw error;
  }
}

/**
 * Query for active games where a specific user is a participant
 * Used for reconnect functionality when a player returns to the multiplayer tab
 *
 * Strategy: Query for games in active states AND check recency using isGameStale()
 */
export async function queryActiveGamesForUser(userId: string): Promise<{ id: string; data: any }[]> {
  const gamesCollection = collection(db, CollectionName);
  const activeGames: { id: string; data: any }[] = [];

  // Active game states - games that are NOT finished
  // IMPORTANT: These must match the State enum values exactly (with spaces!)
  const activeStates = ['Playing', 'Piece Selection', 'King Selection'];

  try {
    // Query for games where user is lightPlayer in each active state
    for (const state of activeStates) {
      const lightPlayerQuery = query(
        gamesCollection,
        where("lightPlayer", "==", userId),
        where("state", "==", state),
        limit(5)
      );

      const lightSnapshot = await getDocs(lightPlayerQuery);
      lightSnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        // Check: not finished (state filter already ensures active, just check lightWon)
        if (data.lightWon === '' || data.lightWon === undefined || data.lightWon === null) {
          // Use staleness utility to filter old games
          if (isGameStale(data)) {
            const age = getGameAge(data);
            const ageMinutes = age ? Math.round(age.ageMs / 60000) : 'unknown';
            return;
          }
          // Skip bot games — they run locally and can't be reconnected to
          if (data.isBotGame) {
            return;
          }
          if (!activeGames.some(g => g.id === docSnapshot.id)) {
            activeGames.push({ id: docSnapshot.id, data: { ...data } });
          }
        }
      });
    }

    // Query for games where user is darkPlayer in each active state
    for (const state of activeStates) {
      const darkPlayerQuery = query(
        gamesCollection,
        where("darkPlayer", "==", userId),
        where("state", "==", state),
        limit(5)
      );

      const darkSnapshot = await getDocs(darkPlayerQuery);
      darkSnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        // Check: not finished (state filter already ensures active, just check lightWon)
        if (data.lightWon === '' || data.lightWon === undefined || data.lightWon === null) {
          // Use staleness utility to filter old games
          if (isGameStale(data)) {
            const age = getGameAge(data);
            const ageMinutes = age ? Math.round(age.ageMs / 60000) : 'unknown';
            return;
          }
          // Skip bot games — they run locally and can't be reconnected to
          if (data.isBotGame) {
            return;
          }
          if (!activeGames.some(g => g.id === docSnapshot.id)) {
            activeGames.push({ id: docSnapshot.id, data: { ...data } });
          }
        }
      });
    }

    return activeGames;
  } catch (error) {
    throw error;
  }
}

export async function getGameStateByGameId(gameId: string): Promise<any> {
  try {
    const docRef = doc(db, CollectionName, gameId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
}

// Get game state only if the game is not finished
// Returns undefined if game is finished (lightWon is set) to prevent loading old games
export async function getActiveGameStateByGameId(gameId: string): Promise<any> {
  const data = await getGameStateByGameId(gameId);
  if (!data) return undefined;

  // Check if game is finished
  // lightWon can be: '' (not finished), 'draw', true, or false
  if (data.lightWon !== '' && data.lightWon !== undefined) {
    return undefined;
  }

  return data;
}

// Function to update chat messages for a game
export async function updateChatMessages(
  gameId: string,
  chatMessages: Array<{username: string, text: string, timestamp: number}>
): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  const chatString = JSON.stringify(chatMessages);

  await setDoc(docRef, { chatMessages: chatString }, { merge: true });
}

// Rematch request functions
export async function sendRematchRequest(
  gameId: string,
  userId: string
): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  await setDoc(docRef, {
    rematchRequestFrom: userId,
    rematchRequestTimestamp: Date.now(),
    rematchAccepted: false,
    rematchDeclined: false
  }, { merge: true });
}

export async function acceptRematchRequest(gameId: string): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  await setDoc(docRef, {
    rematchAccepted: true,
    rematchDeclined: false
  }, { merge: true });
}

export async function declineRematchRequest(gameId: string): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  await setDoc(docRef, {
    rematchAccepted: false,
    rematchDeclined: true
  }, { merge: true });
}

export async function clearRematchRequest(gameId: string): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  await setDoc(docRef, {
    rematchRequestFrom: null,
    rematchRequestTimestamp: null,
    rematchAccepted: false,
    rematchDeclined: false
  }, { merge: true });
}

/**
 * Reset time control values in Firebase for a new game (e.g., after rematch)
 * This clears the old time values so they don't get synced back to overwrite fresh timers
 */
export async function resetTimeControlInFirebase(gameId: string, initialTime: number): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  await setDoc(docRef, {
    lightTimeRemaining: initialTime,
    darkTimeRemaining: initialTime,
    lastMoveTimestamp: null
  }, { merge: true });
}

// Delete a game document (used for test cleanup)
export async function deleteGamesDocument(gameId: string): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  await deleteDoc(docRef);
}

// King Guess taunt feature - sync guesses to Firebase
export interface FirebaseKingGuess {
  pieceId: string;
  position: { row: number; col: number };
  timestamp: number;
}

export async function updateKingGuess(
  gameId: string,
  isLightPlayer: boolean,
  guess: FirebaseKingGuess | null
): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  const field = isLightPlayer ? 'lightPlayerKingGuess' : 'darkPlayerKingGuess';

  // Log the sending action with the specific format requested
  if (guess) {
    if (isLightPlayer) {
    } else {
    }
  }

  await setDoc(docRef, { [field]: guess }, { merge: true });
}

// Post-game king reveal request system - players can request opponent to reveal their king
export async function sendKingRevealRequest(
  gameId: string,
  isLightPlayer: boolean
): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  const prefix = isLightPlayer ? 'light' : 'dark';
  await setDoc(docRef, {
    [`kingRevealRequestBy_${prefix}`]: true,
    [`kingRevealResponseTo_${prefix}`]: null,
  }, { merge: true });
}

export async function respondToKingRevealRequest(
  gameId: string,
  responderIsLight: boolean,
  accepted: boolean
): Promise<void> {
  const docRef = doc(db, CollectionName, gameId);
  const requesterPrefix = responderIsLight ? 'dark' : 'light';
  await setDoc(docRef, {
    [`kingRevealResponseTo_${requesterPrefix}`]: accepted ? 'accepted' : 'declined',
  }, { merge: true });
}

// NOT CURRENTLY USED
// Function to listen for changes in a collection
// export function listenForCollectionChanges(
//   collectionName: string,
//   callback: (data: any[]) => void
// ) {
//   const collectionRef = collection(db, collectionName);
//   return onSnapshot(collectionRef, (querySnapshot) => {
//     const data: any[] = querySnapshot.docs.map(
//       (doc) =>
//         ({
//           id: doc.id,
//           ...doc.data(),
//         } as any)
//     );
//     callback(data);
//   });
// }
